import React, { useState, useEffect, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  propTypes,
  STOCK_INFINITE_MULTIPLE_ITEMS,
  STOCK_MULTIPLE_ITEMS,
  LINE_ITEM_ITEM,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  requireListingImage,
} from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import { getManualStoreReviews } from '../../data/manualStoreReviews';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  resetLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
  handleAddToCart,
} from './ListingPage.shared';
import SectionHero from './SectionHero';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import CustomListingFields from './CustomListingFields';
import ActionBarMaybe from './ActionBarMaybe';

import css from './ListingPage.module.css';
import useAddOrUpdateToCart from '../../hooks/useAddOrUpdateToCart.js';
import { updateCurrentUserProfile } from '../../ducks/user.duck.js';
import ProductAvailabilityBadge from '../../components/ProductAvailabilityBadge/ProductAvailabilityBadge.js';
import ProductOrderForm from '../../components/OrderPanel/ProductOrderForm/ProductOrderForm.js';
import { DEFAULT_STORE_TIMINGS } from '../../util/constants.js';
import { defaultTimeZone } from '../../util/dates.js';
import { PriceMaybe } from '../../components/OrderPanel/OrderPanel.js';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [imageCarouselOpen, setImageCarouselOpen] = useState(false);
  const formRef = useRef(null);

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    onResetLineItems,
    onUpdateCurrentUserProfile,
    ...restOfProps
  } = props;

  const {
    addOrUpdateToCart,
    addOrUpdateToCartInProgress,
    addOrUpdateToCartError,
    cart,
  } = useAddOrUpdateToCart();

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const manualStoreReviews = getManualStoreReviews(currentAuthor?.id?.uuid);
  const reviewsWithManualStoreReviews = [...manualStoreReviews, ...reviews];
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <div>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </div>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });

  const updateDefaultDeliveryAddress = values => {
    const shouldUpdateDefaultDeliveryAddress =
      values.shippingAddress?.selectedPlace?.address &&
      values.shippingAddress?.selectedPlace?.origin;
    const isShipping = values.deliveryMethod === 'shipping';
    if (shouldUpdateDefaultDeliveryAddress && isShipping) {
      onUpdateCurrentUserProfile('publicData', {
        defaultDeliveryAddress: {
          address: values.shippingAddress?.selectedPlace?.address,
          origin: values.shippingAddress?.selectedPlace?.origin,
        },
      });
    }
  };

  const onSubmit = values => {
    updateDefaultDeliveryAddress(values);
    return handleSubmit({
      ...commonParams,
      currentUser,
      callSetInitialValues,
      getListing,
      onInitializeCardPaymentData,
    })({
      ...values,
      orderedProducts: {
        ...(values.deliveryMethod === 'shipping'
          ? {
              shippingAddress: {
                address: values.shippingAddress?.selectedPlace?.address,
                origin: {
                  lat: values.shippingAddress?.selectedPlace?.origin?.lat,
                  lng: values.shippingAddress?.selectedPlace?.origin?.lng,
                },
              },
            }
          : {}),
        authorId: currentAuthor.id.uuid,
        deliveryMethod: values.deliveryMethod,
        listings: {
          [listingId.uuid]: {
            quantity: values.quantity,
          },
        },
      },
    });
  };

  const preventOrderSubmit = fn => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      return fn();
    }
  };

  const handleOrderSubmit = values => preventOrderSubmit(() => onSubmit(values));

  // Cart functionality – add this within the component but before the return statement
  const onAddOrUpdateToCart = values =>
    preventOrderSubmit(() => {
      if (values.quantity === 0) {
        onResetLineItems();
      }
      return handleAddToCart({
        ...commonParams,
        location,
        currentUser,
        addOrUpdateToCart,
        listingId: listingId.uuid,
        authorId: currentAuthor.id.uuid,
        shouldSaveDefaultDeliveryAddress: true,
      })(values);
    });

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  const handleViewPhotosClick = e => {
    // Stop event from bubbling up to prevent image click handler
    // trying to open the carousel as well.
    e.stopPropagation();
    setImageCarouselOpen(true);
  };

  const defaultDeliveryAddress =
    currentUser?.attributes?.profile?.publicData?.defaultDeliveryAddress;

  const actionBar =
    mounted && currentListing.id && isOwnListing ? (
      <>
        {noPayoutDetailsSetWithOwnListing ? (
          <ActionBarMaybe
            className={classNames(css.actionBarForHeroLayout, {
              [css.actionBarNoBorderRadiusOnMobile]: !showListingImage,
            })}
            isOwnListing={isOwnListing}
            listing={currentListing}
            showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
            currentUser={currentUser}
          />
        ) : null}
        <ActionBarMaybe
          className={classNames(css.actionBarForHeroLayout, {
            [css.actionBarNoBorderRadiusOnMobile]: !showListingImage,
          })}
          isOwnListing={isOwnListing}
          listing={currentListing}
          currentUser={currentUser}
          editParams={{
            id: listingId.uuid,
            slug: listingSlug,
            type: listingPathParamType,
            tab: listingTab,
          }}
        />
      </>
    ) : null;

  const preparationHours = publicData?.preparationHours || 0;
  const readyForSale = publicData['ready_for_sale'];

  const { pickupEnabled, shippingEnabled } = publicData || {};

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS, STOCK_INFINITE_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const lineItemUnitType = `line-item/${unitType}`;

  const cartProps = {
    onAddOrUpdateToCart,
    cart,
    addOrUpdateToCartInProgress,
    addOrUpdateToCartError,
  };

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const shouldHavePurchase = isPurchase && lineItemUnitType === LINE_ITEM_ITEM;
  const isClosed = currentListing?.attributes?.state === LISTING_STATE_CLOSED;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm =
    mounted && shouldHavePurchase && !isClosed && typeof currentStock === 'number';

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        {showListingImage ? (
          <SectionHero
            title={title}
            listing={currentListing}
            isOwnListing={isOwnListing}
            imageCarouselOpen={imageCarouselOpen}
            onImageCarouselClose={() => setImageCarouselOpen(false)}
            handleViewPhotosClick={handleViewPhotosClick}
            onManageDisableScrolling={onManageDisableScrolling}
            actionBar={actionBar}
          />
        ) : (
          isOwnListing && <div className={css.actionBarContainerForNoListingImage}>{actionBar}</div>
        )}
        <div className={css.contentWrapperForHeroLayout}>
          <div className={css.mainColumnForHeroLayout}>
            <div className={showListingImage ? css.mobileHeading : css.noListingImageHeadingHero}>
              {showListingImage ? (
                // add css logic here that applies larger margin on mobile view to push down title
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              ) : (
                <H3
                  as="h1"
                  className={classNames(css.orderPanelTitle, {
                    [css.titleMarginForOwnListingNoImage]: isOwnListing,
                  })}
                >
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H3>
              )}
              <PriceMaybe
                className={css.priceMaybe}
                validListingTypes={config.listing.listingTypes}
                price={price}
                publicData={publicData}
                intl={intl}
                marketplaceCurrency={config.currency}
              />
            </div>

            <ProductAvailabilityBadge
              className={css.mobileAvailabilityBadge}
              isNotAvailable={readyForSale === 'no' && preparationHours > 0}
              preparationHours={preparationHours}
            />

            {showProductOrderForm && (
              <div className={css.productOrderFormMobileWrapper}>
                <ProductOrderForm
                  formRef={formRef}
                  formId="OrderPanelProductOrderForm"
                  currentStock={currentStock}
                  allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
                  pickupEnabled={pickupEnabled && displayPickup}
                  shippingEnabled={shippingEnabled && displayShipping}
                  displayDeliveryMethod={displayPickup || displayShipping}
                  onContactUser={onContactUser}
                  authorId={currentAuthor.id}
                  author={currentAuthor}
                  providerUserListing={restOfProps.providerUserListing}
                  fetchProviderUserListingInProgress={
                    restOfProps.fetchProviderUserListingInProgress
                  }
                  fetchProviderUserListingError={restOfProps.fetchProviderUserListingError}
                  storeTimings={
                    currentAuthor?.attributes?.profile?.publicData?.storeTimings ||
                    DEFAULT_STORE_TIMINGS
                  }
                  storeTimeZone={
                    currentAuthor?.attributes?.profile?.publicData?.storeTimeZone ||
                    defaultTimeZone()
                  }
                  defaultDeliveryAddress={defaultDeliveryAddress}
                  lineItemUnitType={lineItemUnitType}
                  onSubmit={handleOrderSubmit}
                  price={price}
                  marketplaceCurrency={config.currency}
                  listingId={currentListing.id}
                  isOwnListing={isOwnListing}
                  marketplaceName={config.marketplaceName}
                  onFetchTransactionLineItems={restOfProps.onFetchTransactionLineItems}
                  lineItems={restOfProps.lineItems}
                  fetchLineItemsInProgress={restOfProps.fetchLineItemsInProgress}
                  fetchLineItemsError={restOfProps.fetchLineItemsError}
                  payoutDetailsWarning={payoutDetailsWarning}
                  {...cartProps}
                />
              </div>
            )}

            <SectionTextMaybe text={description} showAsIngress />

            <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            />

            <SectionMapMaybe
              geolocation={
                geolocation ||
                currentAuthor?.attributes?.profile?.publicData?.geolocation
              }
              publicData={
                publicData?.location
                  ? publicData
                  : {
                      ...publicData,
                      location:
                        currentAuthor?.attributes?.profile?.publicData?.location,
                    }
              }
              listingId={currentListing.id}
              mapsConfig={config.maps}
            />
            <SectionReviews
              reviews={reviewsWithManualStoreReviews}
              fetchReviewsError={fetchReviewsError}
            />
            <SectionAuthorMaybe
              title={title}
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => setInquiryModalOpen(false)}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              onManageDisableScrolling={onManageDisableScrolling}
            />
          </div>
          <div className={css.orderColumnForHeroLayout}>
            <OrderPanel
              defaultDeliveryAddress={defaultDeliveryAddress}
              className={css.orderPanel}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="div" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              showListingImage={showListingImage}
              cartProps={cartProps}
              shouldHideOrderPanelMobileCTA
            />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with cover photo layout
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Object} props.currentUser - The current user
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error

 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{
          from: `${location.pathname}${location.search}${location.hash}`,
        }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    providerUserListingId,
    fetchProviderUserListingInProgress,
    fetchProviderUserListingError,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const [providerUserListing] = getMarketplaceEntities(state, [
    { id: providerUserListingId, type: 'listing' },
  ]);

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
    providerUserListing,
    fetchProviderUserListingInProgress,
    fetchProviderUserListingError,
    providerUserListing,
    fetchProviderUserListingInProgress,
    fetchProviderUserListingError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
  onResetLineItems: () => dispatch(resetLineItems()), // for OrderPanel,
  onUpdateCurrentUserProfile: (key, value) => dispatch(updateCurrentUserProfile(key, value)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;
